import {Result, ResultError, ResultValue} from 'src_common/common/mobx-utils/Form';

export const validateRequire = (value: string): Result<string> => {
    if (value.trim().length < 1) {
        return new ResultError('This field is required');
    }
    return new ResultValue(value);
};

export const validateSpaces = (value: string): Result<string> => {
    if (/\s/.test(value)) {
        return new ResultError('This field cannot has whitespaces');
    }

    return new ResultValue(value);
};

export const validateSpecialCharactersInUrl = (value: string): Result<string> => {
    const regex = /^[a-zA-Z0-9-]+$/;

    if (regex.test(value)) {
        return new ResultValue(value);
    }
    return new ResultError('This field can only include char, number, -');
};


export const validateLength = (length: number) => (value: string): Result<string> => {
    if (value.length > length) {
        return new ResultError(`Max length is ${length}`);
    }
    return new ResultValue(value);
};

export const validateRequireFile = (value: boolean): Result<boolean> => {
    if (!value) {
        return new ResultError('This field is required');
    }
    return new ResultValue(value);
};

export const validateIsNumber = (value: string): Result<string> => {
    const number = Number(value);
    if (isNaN(number)) {
        return new ResultError('Invalid number');
    }
    return new ResultValue(value);
};

export const validateStringLength = (value: string): Result<string> => {
    const valueStr = value.replace(/\s+/g, ' ').trim();
    if (valueStr.length > 30) {
        return new ResultError('Maximum 30 characters');
    }
    return new ResultValue(value);
};

export const validateStringLength20 = (value: string): Result<string> => {
    const valueStr = value.replace(/\s+/g, ' ').trim();
    if (valueStr.length > 20) {
        return new ResultError('Maximum 20 characters');
    }
    return new ResultValue(value);
};

export const validateStringLength30 = (value: string): Result<string> => {
    if (value === '') {
        return new ResultError('Field required.');
    }
    const valueStr = value.replace(/\s+/g, ' ').trim();
    if (valueStr.length > 30) {
        return new ResultError('Maximum 30 characters');
    }
    return new ResultValue(value);
};

export const validateStringLength255 = (value: string): Result<string> => {
    const valueStr = value.replace(/\s+/g, ' ').trim();
    if (valueStr.length > 255) {
        return new ResultError('Maximum 255 characters');
    }
    return new ResultValue(value);
};

export const validateExpectNumber = (value: string): Result<number | null> => {

    const valueNumber = Number(value);

    if (isNaN(valueNumber)) {
        return new ResultError('Number expected');
    }

    return new ResultValue(valueNumber);
};


export const validateExpectDecimalNumber = (value: string): Result<number | null> => {
    if (value === '') {
        return new ResultError('Field required.');
    }
    const valueNumber = Number(value);

    if (isNaN(valueNumber) || value.includes(',') || value.includes('.') === false || value.length < 3) {
        return new ResultError('Decimal number expected');
    }

    return new ResultValue(valueNumber);
};

export const validateExpectPositiveNumber = (value: string): Result<number | null> => {
    if (value === '') {
        return new ResultValue(null);
    }

    if (value.includes(',')) {
        return new ResultError('Wrong number format');
    }

    const valueNumber = Number(value);

    if (isNaN(valueNumber)) {
        return new ResultError('Number expected');
    }
    if (valueNumber <= 0) {
        return new ResultError('Number has to be greater than 0');
    }

    return new ResultValue(valueNumber);
};

export const validateExpectValueInPx = (value: string): Result<string> => {
    if (value === '') {
        return new ResultValue(null);
    }
    const valueSplit = value.split('px');

    if (valueSplit.length !== 2) {
        return new ResultError('Value in px expected');
    }

    const valueNumber = Number(valueSplit[0]);

    if (isNaN(valueNumber)) {
        return new ResultError('Value in px expected');
    }
    if (valueSplit[1] !== '') {
        return new ResultError('Value in px expected');
    }

    return new ResultValue(value);
};

const isValidHttpUrl = (value: string): boolean => {
    let url;

    try {
        url = new URL(value);
    } catch (_) {
        return false;
    }

    return url.protocol === 'http:' || url.protocol === 'https:';
};

export const validateExpectLink = (value: string): Result<string> => {
    if (value === '') {
        return new ResultValue(value);
    }

    if (!isValidHttpUrl(value)) {
        return new ResultError('Link expected');
    }

    return new ResultValue(value);
};

export const validateExpectHexColor = (value: string): Result<string> => {
    if (value === '') {
        return new ResultValue(value);
    }

    if (value.startsWith('#') && value.length === 7) {
        if (!isNaN(Number('0x' + value.slice(1)))) {
            return new ResultValue(value);
        }
    }

    return new ResultError('Hex expected');
};

export const validateListOfNumbersWithLimit = (limit: number) => (value: string): Result<string> => {
    if (value === '') {
        return new ResultValue(value);
    }

    if (value.includes(' ')) {
        return new ResultError('Do not use space.');
    }

    if (value.includes('.')) {
        return new ResultError('Do not use dot.');
    }

    const stringValueList = value.replace(/\n/g,',').split(',');
    const wrongStringsList = [];
    const correctNumbers: Set<number> = new Set();

    for (const stringValue of stringValueList) {
        if (stringValue.length > 0) {
            const numberValue = parseInt(stringValue, 10);

            if (isNaN(numberValue) || numberValue.toString().length !== stringValue.length) {
                wrongStringsList.push(stringValue);
            } else {
                correctNumbers.add(numberValue);
            }
        }
    }

    if (wrongStringsList.length > 0) {
        return new ResultError(`Wrong numbers: ${wrongStringsList.join(', ')}`);
    }

    if (correctNumbers.size > limit) {
        return new ResultError(`Too many numbers. Limit is: ${limit}`);
    }

    const resultValue = Array.from(correctNumbers).join(',').toString();

    return new ResultValue(resultValue);
};

export const validateAppVersion = (value: string): Result<string> => {
    if (value === '') {
        return new ResultValue(value);
    }
    if (value.length === 0) {
        return new ResultValue(value);
    }

    const firstLetter = Number(value[0]);
    const lastLetter = Number(value[value.length - 1]);

    if (isNaN(firstLetter) || isNaN(lastLetter)) {
        return new ResultError('First and last letter have to be a number - ex. - 1.2.3');
    }

    const valueWithoutDots = Number(value.replaceAll('.', ''));

    if (isNaN(valueWithoutDots)) {
        return new ResultError('Version can contain only dots and numbers - ex. - 1.2.3');
    }

    return new ResultValue(value);
};
